import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyOrganizations } from '_/api/organizations/organizations.api';
import zeusSdk from '_/utils/SDKs';
import React, { createContext, useContext, ReactNode, ReactElement, useMemo, useEffect } from 'react';

// Role constants
export const OrgRole = {
  ADMIN: 'ADMIN',
  OPS: 'OPS',
  SCHEMA_ADMIN: 'SCHEMA_ADMIN',
  MEMBER: 'MEMBER',
  SOURCE_ENGINEER: 'SOURCE_ENGINEER',
} as const;

export const SystemRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  DEVELOPER: 'DEVELOPER',
} as const;

// Role groups for permission checking
export const OPS_INCLUDED = [OrgRole.ADMIN, OrgRole.OPS];
export const MEMBER_NOT_INCLUDED = [OrgRole.ADMIN, OrgRole.SCHEMA_ADMIN, OrgRole.OPS];
export const SOURCE_ENGINEER_NOT_INCLUDED = [OrgRole.ADMIN, OrgRole.SCHEMA_ADMIN, OrgRole.MEMBER, OrgRole.OPS];
export const SUPER_ADMIN_ALLOWED = [SystemRole.DEVELOPER, SystemRole.SUPER_ADMIN];

export interface User {
  id?: string | null;
  name?: string;
  email?: string;
  username: string;
  platformUserId?: string;
  avatarUrl?: string;
  homePage?: string;
  githubUsername?: string;
  githubId?: string;
}

interface CurrentUserResponse {
  user: User;
  superAdmin: boolean;
  roles: string[];
}

interface AuthContextValue {
  currentUser: User | null;
  isLoading: boolean;
  systemRoles: string[];
  isSuperAdmin: boolean;
  isDeveloper: boolean;

  // Org role checkers (require orgId parameter)
  getOrgRoles: (orgId: string) => string[];
  hasOrgRole: (orgId: string, role: string) => boolean;
  hasAnyOrgRole: (orgId: string, roles: string[]) => boolean;
  isOrgAdmin: (orgId: string) => boolean;
  isOrgOps: (orgId: string) => boolean;
  isSchemaAdmin: (orgId: string) => boolean;
  isMember: (orgId: string) => boolean;
  isSourceEngineer: (orgId: string) => boolean;

  // Access checkers (require orgId parameter)
  checkOrgAccess: (orgId: string, allowedRoles: string | string[]) => boolean;
  checkSystemAccess: (allowedRoles: string | string[]) => boolean;

  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchCurrentUser(): Promise<CurrentUserResponse> {
  return zeusSdk.auth.currentUser();
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const {
    data: authData,
    isLoading: isLoadingUser,
    error,
    refetch,
  } = useQuery<CurrentUserResponse>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });

  // whoami does not return org roles - they come from the user's OrgUser rows joined onto /orgs/_mine.
  // Same query key as useMyOrganizations so both share the cache and its invalidations.
  const { data: myOrganizations, isLoading: isLoadingMyOrganizations } = useQuery({
    queryKey: ['organizations', 'my'],
    queryFn: fetchMyOrganizations,
    enabled: !!authData?.user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const isLoading = isLoadingUser || (!!authData?.user && isLoadingMyOrganizations);

  // Redirect to login page immediately when receiving 401 Unauthorized
  useEffect(() => {
    if (error && (error as any)?.status === 401 && !window.location.pathname.startsWith('/login')) {
      const currentUrl = window.location.href;
      window.location.assign(`/login?redirectUrl=${encodeURIComponent(currentUrl)}`);
    }
  }, [error]);

  // Configure Sentry with user information
  useEffect(() => {
    if (authData?.user) {
      const { email, id, username } = authData.user;
      Sentry.setUser({
        email: email,
        id: id || undefined,
        username: username,
      });
    }
  }, [authData]);

  const value = useMemo<AuthContextValue>(() => {
    const currentUser = authData?.user || null;
    const orgRolesById: Record<string, string[]> = {};
    (myOrganizations || []).forEach((org) => {
      orgRolesById[org.id] = org.roles?.roles || [];
    });
    const systemRoles = authData?.roles || [];
    const isSuperAdmin = authData?.superAdmin || false;
    const isDeveloper = systemRoles.includes(SystemRole.DEVELOPER);

    // Get org roles for a specific organization
    const getOrgRoles = (orgId: string): string[] => {
      if (!orgId) {
        return [];
      }
      return orgRolesById[orgId] || [];
    };

    // Check if user has a specific role in an org
    const hasOrgRole = (orgId: string, role: string): boolean => {
      return getOrgRoles(orgId).includes(role);
    };

    // Check if user has any of the specified roles in an org
    const hasAnyOrgRole = (orgId: string, roles: string[]): boolean => {
      const userRoles = getOrgRoles(orgId);
      return roles.some((role) => userRoles.includes(role));
    };

    // Specific role checkers
    const isOrgAdmin = (orgId: string): boolean => hasOrgRole(orgId, OrgRole.ADMIN);
    const isOrgOps = (orgId: string): boolean => hasOrgRole(orgId, OrgRole.OPS);
    const isSchemaAdmin = (orgId: string): boolean => hasOrgRole(orgId, OrgRole.SCHEMA_ADMIN);
    const isMember = (orgId: string): boolean => hasOrgRole(orgId, OrgRole.MEMBER);
    const isSourceEngineer = (orgId: string): boolean => hasOrgRole(orgId, OrgRole.SOURCE_ENGINEER);

    // Check org access with allowed roles
    const checkOrgAccess = (orgId: string, allowedRoles: string | string[]): boolean => {
      const formattedAllowedRoles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
      const userRoles = getOrgRoles(orgId);

      // Super admin always has access (unless only DEVELOPER role is specified)
      const superAdminAccess =
        formattedAllowedRoles.includes(SystemRole.DEVELOPER) && formattedAllowedRoles.length === 1 ? false : isSuperAdmin;

      const access = userRoles.length ? formattedAllowedRoles.some((role) => userRoles.includes(role)) : false;
      const result = superAdminAccess || access;
      return result;
    };

    // Check system access with allowed roles
    const checkSystemAccess = (allowedRoles: string | string[]): boolean => {
      const formattedAllowedRoles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

      // Super admin always has access (unless only DEVELOPER role is specified)
      const superAdminAccess =
        formattedAllowedRoles.includes(SystemRole.DEVELOPER) && formattedAllowedRoles.length === 1 ? false : isSuperAdmin;

      const access = systemRoles.length ? formattedAllowedRoles.some((role) => systemRoles.includes(role)) : false;
      return superAdminAccess || access;
    };

    return {
      currentUser: currentUser,
      isLoading: isLoading,
      systemRoles: systemRoles,
      isSuperAdmin: isSuperAdmin,
      isDeveloper: isDeveloper,
      getOrgRoles: getOrgRoles,
      hasOrgRole: hasOrgRole,
      hasAnyOrgRole: hasAnyOrgRole,
      isOrgAdmin: isOrgAdmin,
      isOrgOps: isOrgOps,
      isSchemaAdmin: isSchemaAdmin,
      isMember: isMember,
      isSourceEngineer: isSourceEngineer,
      checkOrgAccess: checkOrgAccess,
      checkSystemAccess: checkSystemAccess,
      refetch: refetch,
    };
  }, [authData, myOrganizations, isLoading, refetch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
